const FENCE_LINE_REGEX = /^(?: {0,3}>[ \t]?)* {0,3}(`{3,}|~{3,})(.*)$/
const INDENTED_CODE_LINE_REGEX = /^(?: {0,3}>[ \t]?)*(?: {4}|\t)/
const HEADING_LINE_REGEX = /^#{1,6}\s+\S/
const BOLD_HEADING_REGEX = /^(\*\*|__)(?!\s).*\S\1$/
const HEADING_PREFIX_REGEX = /^#{1,6}\s+/
const BLOCKQUOTE_PREFIX_REGEX = /^>\s?/
const LIST_PREFIX_REGEX = /^(?:[-+*]|\d+\.)\s+/
const MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\([^)]+\)/g
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\([^)]+\)/g
const AUTOLINK_REGEX = /<((?:https?:\/\/|mailto:)[^>\s]+)>/g

const MIN_SENTENCE_CHARS = 10
const SENTENCE_BOUNDARY_REGEX = /[.!?](?=\s|$)/g
const ABBREVIATION_REGEX = /(?:\b(?:e\.g|i\.e|etc|vs|cf|approx)|\b[A-Za-z])\.$/i
const TERMINAL_ETC_REGEX = /\betc\.$/i
export const MAX_PREVIEW_LENGTH = 80
const MAX_PREVIEW_INPUT_LENGTH = 8_192
const MAX_FENCE_LOOKBACK = 32_768

function stripMarkdownLinePrefixes(line: string): string {
  let result = line.trim()

  while (true) {
    const stripped = result
      .replace(HEADING_PREFIX_REGEX, '')
      .replace(BLOCKQUOTE_PREFIX_REGEX, '')
      .replace(LIST_PREFIX_REGEX, '')
      .trim()

    if (stripped === result) return result
    result = stripped
  }
}

function stripMarkdownInlineText(text: string): string {
  return text
    .replace(MARKDOWN_IMAGE_REGEX, '$1')
    .replace(MARKDOWN_LINK_REGEX, '$1')
    .replace(AUTOLINK_REGEX, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim()
}

function isHeadingLine(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false
  return HEADING_LINE_REGEX.test(trimmed) || BOLD_HEADING_REGEX.test(trimmed)
}

function isListOrTableLine(line: string): boolean {
  const trimmed = line.trim()
  return (
    trimmed.startsWith('- ') ||
    (trimmed.startsWith('* ') && !trimmed.startsWith('**')) ||
    (trimmed.startsWith('+ ') && !trimmed.startsWith('++')) ||
    trimmed.startsWith('|') ||
    /^\d+\.\s/.test(trimmed)
  )
}

function extractFirstSentence(paragraph: string): string | null {
  const stripped = paragraph
    .split('\n')
    .filter(line => !INDENTED_CODE_LINE_REGEX.test(line) && !isHeadingLine(line) && !isListOrTableLine(line))
    .map(line => stripMarkdownInlineText(stripMarkdownLinePrefixes(line)))
    .filter(Boolean)
    .join(' ')
  if (!stripped) return null

  let sentence: string | null = null
  for (const match of stripped.matchAll(SENTENCE_BOUNDARY_REGEX)) {
    if (match.index === undefined || match.index + 1 < MIN_SENTENCE_CHARS) continue

    const candidate = stripped.slice(0, match.index + 1)
    if (match[0] === '.' && ABBREVIATION_REGEX.test(candidate)) {
      const hasFollowingText = stripped.slice(match.index + 1).trim().length > 0
      if (hasFollowingText || !TERMINAL_ETC_REGEX.test(candidate)) continue
    }

    sentence = candidate
    break
  }

  if (!sentence) return null
  if (sentence.length <= MAX_PREVIEW_LENGTH) return sentence

  const truncated = sentence.slice(0, MAX_PREVIEW_LENGTH)
  const lastSpace = truncated.lastIndexOf(' ')
  return `${lastSpace > 20 ? truncated.slice(0, lastSpace) : truncated}…`
}

type FenceState = {delimiter: string; length: number} | null

function nextFenceState(line: string, state: FenceState): FenceState {
  const match = FENCE_LINE_REGEX.exec(line)
  if (!match?.[1]) return state

  const run = match[1]
  const delimiter = run.startsWith('`') ? '`' : '~'
  const info = match[2] ?? ''

  if (state === null) {
    if (delimiter === '`' && info.includes('`')) return state
    return {delimiter, length: run.length}
  }

  if (delimiter === state.delimiter && run.length >= state.length && info.trim() === '') return null
  return state
}

function scanFenceState(text: string, state: FenceState): FenceState {
  let current = state
  for (const line of text.split('\n')) {
    current = nextFenceState(line, current)
  }
  return current
}

function stripFencedCode(text: string, state: FenceState): string {
  const lines = text.split('\n')
  const kept: string[] = []
  let current = state

  for (const line of lines) {
    const next = nextFenceState(line, current)
    const isProse = current === null && next === current
    kept.push(isProse ? line : '')
    current = next
  }

  return kept.join('\n')
}

function normalizeNewlines(text: string): string {
  return text.includes('\r') ? text.replaceAll('\r\n', '\n') : text
}

function extractReasoningTail(reasoningText: string): string {
  const start = Math.max(0, reasoningText.length - MAX_PREVIEW_INPUT_LENGTH)
  if (start === 0) return stripFencedCode(normalizeNewlines(reasoningText), null)

  const newlineIndex = reasoningText.indexOf('\n', start)
  const tailStart = newlineIndex === -1 ? start : newlineIndex + 1
  const lookbackStart = Math.max(0, tailStart - MAX_FENCE_LOOKBACK)
  const state = scanFenceState(normalizeNewlines(reasoningText.slice(lookbackStart, tailStart)), null)

  return stripFencedCode(normalizeNewlines(reasoningText.slice(tailStart)), state)
}

export function extractReasoningPreview(reasoningText: string | undefined): string | null {
  if (!reasoningText?.trim()) return null

  const paragraphs = extractReasoningTail(reasoningText)
    .split(/\n\n+/)
    .filter(paragraph => paragraph.trim())

  for (let index = paragraphs.length - 1; index >= 0; index--) {
    const paragraph = paragraphs[index]
    if (!paragraph) continue

    const sentence = extractFirstSentence(paragraph)
    if (sentence) return sentence
  }

  return null
}
